import axios from 'axios';
import config from '../config';
import {
  AttributeOption,
  AttributeType,
  BrandOption,
  InternalProductOption,
  InternalSkuOption,
  PurchaseOrder,
  PurchaseOrderStatsBucket,
  PurchaseOrderStatsResponse,
  PurchaseOrderDeliveryMonthDrilldownResponse,
  PurchaseOrdersResponse,
  PurchaseOrderImportDecisions,
  PurchaseOrderImportMapping,
  PurchaseOrderImportOptions,
  PurchaseOrderImportParseResponse,
  PurchaseOrderImportValidateResponse,
  SupplierOption,
} from '../types/purchasing';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

const API_BASE = `${config.API_URL}/purchasing`;

export async function fetchPurchaseOrders(params: {
  page?: number;
  limit?: number;
  status?: string | string[];
  viskanStatus?: string | string[];
  readyForTransfer?: boolean;
  supplierId?: number;
  season?: string;
}): Promise<PurchaseOrdersResponse> {
  const normalizeStatusParam = (
    value: string | string[] | undefined,
  ): string | undefined => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      const combined = value
        .flatMap((v) => v.split(','))
        .map((v) => v.trim())
        .filter(Boolean);
      return combined.length > 0 ? combined.join(',') : undefined;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  };

  const response = await axios.get(`${API_BASE}/purchase-orders`, {
    params: {
      page: params.page || 1,
      limit: params.limit || 20,
      status: normalizeStatusParam(params.status),
      viskanStatus: normalizeStatusParam(params.viskanStatus),
      readyForTransfer:
        params.readyForTransfer === undefined
          ? undefined
          : params.readyForTransfer
            ? '1'
            : '0',
      supplierId: params.supplierId,
      season: params.season,
    },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function fetchPurchaseOrderStats(params: {
  bucket: PurchaseOrderStatsBucket;
  season?: string;
  supplierId?: number;
  deliveryMonthFrom?: string;
  deliveryMonthTo?: string;
}): Promise<PurchaseOrderStatsResponse> {
  const response = await axios.get(`${API_BASE}/purchase-orders/stats`, {
    params,
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function fetchPurchaseOrdersByDeliveryMonth(args: {
  month: string; // YYYY-MM
  bucket: PurchaseOrderStatsBucket;
  season?: string;
  supplierId?: number;
  deliveryMonthFrom?: string;
  deliveryMonthTo?: string;
}): Promise<PurchaseOrderDeliveryMonthDrilldownResponse> {
  const { month, ...params } = args;
  const response = await axios.get(
    `${API_BASE}/purchase-orders/stats/delivery-month/${encodeURIComponent(month)}`,
    {
      params,
      headers: getAuthHeaders(),
    },
  );
  return response.data;
}

export async function fetchPurchaseOrderById(
  id: number,
): Promise<PurchaseOrder> {
  const response = await axios.get(`${API_BASE}/purchase-orders/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function createPurchaseOrder(
  payload: Partial<PurchaseOrder>,
): Promise<PurchaseOrder> {
  const response = await axios.post(`${API_BASE}/purchase-orders`, payload, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function updatePurchaseOrder(
  id: number,
  payload: Partial<PurchaseOrder>,
): Promise<PurchaseOrder> {
  const response = await axios.put(
    `${API_BASE}/purchase-orders/${id}`,
    payload,
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
}

export async function submitPurchaseOrder(id: number): Promise<PurchaseOrder> {
  const response = await axios.post(
    `${API_BASE}/purchase-orders/${id}/submit`,
    {},
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function transferPurchaseOrder(
  id: number,
  options?: { forceMissingSize?: boolean },
): Promise<{
  status: 'ok';
  purchaseOrderId: number;
  results: Array<{
    productNumber: string;
    status: 'success' | 'failed';
    skuCount: number;
    priceUpdatesCount: number;
  }>;
  generatedPluNumbers: Array<{
    purchaseLineSkuId: number;
    articleNumber: string;
    pluNo: string;
  }>;
}> {
  const response = await axios.post(
    `${API_BASE}/purchase-orders/${id}/transfer`,
    { forceMissingSize: options?.forceMissingSize === true },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function deletePurchaseOrder(id: number): Promise<void> {
  await axios.delete(`${API_BASE}/purchase-orders/${id}`, {
    headers: getAuthHeaders(),
  });
}

export async function fetchSuppliers(): Promise<SupplierOption[]> {
  const response = await axios.get(`${API_BASE}/suppliers`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function fetchSupplierBrands(
  supplierId: number,
): Promise<BrandOption[]> {
  const response = await axios.get(
    `${API_BASE}/suppliers/${supplierId}/brands`,
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
}

export async function fetchAttributes(): Promise<{
  attribute1: AttributeOption[];
  attribute2: AttributeOption[];
  attribute3: AttributeOption[];
}> {
  const response = await axios.get(`${API_BASE}/attributes`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function fetchNextAttributeNumber(): Promise<string> {
  const response = await axios.get(
    `${config.API_URL}/attribute-values/next-number`,
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data.nextNumber;
}

export async function createAttributeValue(args: {
  attributeType: AttributeType;
  attributeCode: string;
  attributeNumber: string;
}): Promise<AttributeOption> {
  const response = await axios.post(
    `${config.API_URL}/attribute-values`,
    args,
    {
      headers: getAuthHeaders(),
    },
  );
  const created = response.data.attributeValue;
  return {
    code: created.attributeCode,
    number: created.attributeNumber,
  };
}

export async function searchCatalogProducts(params: {
  supplierId: number;
  q?: string;
  brand?: string;
  limit?: number;
}): Promise<InternalProductOption[]> {
  const response = await axios.get(`${API_BASE}/catalog/products`, {
    params,
    headers: getAuthHeaders(),
  });
  return response.data;
}

export type ExactCatalogProductLookupResult =
  | { exists: false; productNumber: string }
  | {
      exists: true;
      productId: number;
      productNumber: string;
      productName: string | null;
      brandName: string | null;
      supplierId: number | null;
      supplierName: string | null;
    };

export async function lookupExactCatalogProduct(
  productNumber: string,
): Promise<ExactCatalogProductLookupResult> {
  const response = await axios.get(`${API_BASE}/catalog/products/exact`, {
    params: { productNumber },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function fetchCatalogProductSkus(
  productNumber: string,
): Promise<InternalSkuOption[]> {
  const response = await axios.get(
    `${API_BASE}/catalog/products/${productNumber}/skus`,
    {
      headers: getAuthHeaders(),
    },
  );
  return response.data;
}

export type CatalogSystemPriceSummary =
  | { available: false; productNumber: string }
  | {
      available: true;
      productNumber: string;
      supplierPrice: {
        distinctCount: number;
        min: number | null;
        max: number | null;
      };
      priceOrig: {
        distinctCount: number;
        min: number | null;
        max: number | null;
      };
      price: { distinctCount: number; min: number | null; max: number | null };
    };

export async function fetchCatalogProductPriceSummary(
  productNumber: string,
): Promise<CatalogSystemPriceSummary> {
  const response = await axios.get(
    `${API_BASE}/catalog/products/${encodeURIComponent(productNumber)}/prices`,
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function getNextPlu(
  articleNumber: string,
  existingPluNumbers: string[],
  currentPlu?: string,
): Promise<{ nextPlu: string }> {
  const response = await axios.post(
    `${API_BASE}/next-plu/${encodeURIComponent(articleNumber)}`,
    { existingPluNumbers, currentPlu },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function fetchSeasons(): Promise<string[]> {
  const response = await axios.get(`${API_BASE}/seasons`, {
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function createSeason(code: string): Promise<{ code: string }> {
  const response = await axios.post(
    `${API_BASE}/seasons`,
    { code },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function searchSupplierApiProducts(
  supplierId: number,
  query: string,
): Promise<unknown[]> {
  const response = await axios.get(
    `${API_BASE}/supplier-api/${supplierId}/search`,
    {
      params: { q: query },
      headers: getAuthHeaders(),
    },
  );
  return response.data;
}

// ── Purchase order Excel import ───────────────────────────────────────────────

export async function parsePurchaseOrderImportExcel(
  file: File,
  supplierId: number,
): Promise<PurchaseOrderImportParseResponse> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('supplierId', String(supplierId));
  const response = await axios.post(
    `${API_BASE}/purchase-orders/import/parse`,
    formData,
    {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
}

export async function validatePurchaseOrderImport(args: {
  supplierId: number;
  file: File;
  mapping?: PurchaseOrderImportMapping;
  decisions?: PurchaseOrderImportDecisions;
  defaults?: {
    brandName?: string;
  };
  options?: PurchaseOrderImportOptions;
}): Promise<PurchaseOrderImportValidateResponse> {
  const formData = new FormData();
  formData.append('file', args.file, args.file.name);
  formData.append('supplierId', String(args.supplierId));
  if (args.mapping) formData.append('mapping', JSON.stringify(args.mapping));
  if (args.decisions)
    formData.append('decisions', JSON.stringify(args.decisions));
  if (args.defaults) formData.append('defaults', JSON.stringify(args.defaults));
  if (args.options) formData.append('options', JSON.stringify(args.options));
  const response = await axios.post(
    `${API_BASE}/purchase-orders/import/validate`,
    formData,
    {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
}

export async function createPurchaseOrderFromImport(args: {
  supplierId: number;
  file: File;
  mapping?: PurchaseOrderImportMapping;
  season?: string | null;
  decisions: PurchaseOrderImportDecisions;
  defaults?: {
    brandName?: string;
  };
  options?: PurchaseOrderImportOptions;
}): Promise<PurchaseOrder> {
  const formData = new FormData();
  formData.append('file', args.file, args.file.name);
  formData.append('supplierId', String(args.supplierId));
  if (args.mapping) formData.append('mapping', JSON.stringify(args.mapping));
  if (args.season) formData.append('season', args.season);
  formData.append('decisions', JSON.stringify(args.decisions));
  if (args.defaults) formData.append('defaults', JSON.stringify(args.defaults));
  if (args.options) formData.append('options', JSON.stringify(args.options));
  const response = await axios.post(
    `${API_BASE}/purchase-orders/import/create`,
    formData,
    {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
}

export async function downloadPurchaseOrderNotePdf(args: {
  purchaseOrderId: number;
}): Promise<Blob> {
  const response = await axios.get(
    `${API_BASE}/purchase-orders/${args.purchaseOrderId}/note.pdf`,
    {
      headers: getAuthHeaders(),
      responseType: 'blob',
    },
  );
  return response.data;
}
